// Section 22 left panel: a compact list of everyone with status glyphs, filterable by household
// or job. Hovering (or focusing) a row highlights that person across panels.
import type { HouseholdId, JobId, PersonId } from '../../sim/api';
import type { ListFilter } from '../../store';
import type { PeopleListVM } from '../../viewModels';
import { uiText } from '../text/en';
import { Glyphs } from './glyphs';

interface Props {
  vm: PeopleListVM;
  filter: ListFilter;
  hovered: PersonId | null;
  selected: PersonId | null;
  onFilter: (filter: ListFilter) => void;
  onHover: (id: PersonId | null) => void;
  onOpen: (id: PersonId) => void;
}

function filterValue(f: ListFilter): string {
  return f.kind === 'all' ? 'all' : f.kind === 'household' ? `h:${f.householdId}` : `j:${f.jobId}`;
}

function parseFilter(value: string): ListFilter {
  if (value.startsWith('h:')) return { kind: 'household', householdId: value.slice(2) as HouseholdId };
  if (value.startsWith('j:')) return { kind: 'job', jobId: value.slice(2) as JobId };
  return { kind: 'all' };
}

export function PeopleList({ vm, filter, hovered, selected, onFilter, onHover, onOpen }: Props) {
  const t = uiText.list;
  return (
    <section class="panel plist" aria-labelledby="plist-title">
      <h2 id="plist-title" class="panel__title">
        {t.title}
      </h2>
      <label class="plist__filter">
        <span class="field__label">{t.filter}</span>
        <select value={filterValue(filter)} onChange={(e) => onFilter(parseFilter(e.currentTarget.value))}>
          <option value="all">{t.all}</option>
          <optgroup label={t.byHousehold}>
            {vm.households.map((h) => (
              <option key={h.id} value={`h:${h.id}`}>
                {t.household(h.name, h.size)}
              </option>
            ))}
          </optgroup>
          <optgroup label={t.byJob}>
            {vm.jobs.map((j) => (
              <option key={j.id} value={`j:${j.id}`}>
                {t.job(j.label, j.count)}
              </option>
            ))}
          </optgroup>
        </select>
      </label>
      <p class="hint plist__count">{t.count(vm.rows.length, vm.total)}</p>
      <ul class="plist__rows">
        {vm.rows.map((r) => (
          <li
            key={r.id}
            class={`plist__row${r.id === hovered ? ' is-hovered' : ''}${r.id === selected ? ' is-selected' : ''}`}
            onMouseEnter={() => onHover(r.id)}
            onMouseLeave={() => onHover(null)}
          >
            <button
              type="button"
              class="plist__name link"
              aria-label={t.open(r.name)}
              onClick={() => onOpen(r.id)}
              onFocus={() => onHover(r.id)}
              onBlur={() => onHover(null)}
            >
              <span class="name">{r.name}</span>
            </button>
            <span class="plist__meta">
              <span class="num">{r.ageYears}</span> {r.jobLabel.toLowerCase()}
            </span>
            <Glyphs flags={r.flags} />
          </li>
        ))}
      </ul>
    </section>
  );
}
